import { AdvertisementResponse } from '../../../api'
import { BulkPassRequest } from '../../../services/queries/useBulkCreatePasses'
import { parseBackFieldsFromRow } from './backFieldsParser'
import { CsvRow } from './csvUtils'
import { convertToISOString } from './dateUtils'
import { parseNotificationsFromRow } from './notificationParser'

const DEFAULT_AD_DURATION_DAYS = 30

/**
 * Generates a 4-character alphanumeric Google Ad ID
 */
export const generateGoogleAdId = (): string => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
  let result = ''
  for (let i = 0; i < 4; i += 1) {
    result += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return result
}

interface BuildAdParams {
  row: CsvRow
  rowAdDetails: AdvertisementResponse | null
  globalIndex: number
  imageUploadCache: Map<string, string>
}

/**
 * Builds an ad request object from a CSV row
 */
export const buildAdFromRow = ({
  row,
  rowAdDetails,
  globalIndex,
  imageUploadCache,
}: BuildAdParams): BulkPassRequest => {
  const rowAdvertId = row.AdvertId || row.advertId

  const passDetails = rowAdDetails?.passDetails || {
    passColor: '#000000',
    fontColor: '#FFFFFF',
    appleWalletImage: '',
    googleWalletImage: '',
    applePassLogo: '',
    googlePassLogo: '',
    appStoreId: '',
    playStoreUrl: '',
    clickThroughUrl: '',
    offerRedemption: {
      enable: true,
      codeType: 'BARCODE_128' as const,
      offerName: '',
      offerCodeUrl: '',
    },
  }
  const offerRedemption = passDetails.offerRedemption || {
    enable: true,
    codeType: 'BARCODE_128' as const,
    offerName: '',
    offerCodeUrl: '',
  }

  // Get image URLs from CSV.
  // Images: prefer AppleWalletImage / GoogleWalletImage, then *ImageUrl fallbacks.
  // Logos: prefer ApplePassLogo / GooglePassLogo, then AppleWalletLogoUrl / GoogleWalletLogoUrl fallbacks.
  const appleImageUrl =
    row.AppleWalletImage || row.appleWalletImage || row.AppleWalletImageUrl || row.appleWalletImageUrl || ''
  const googleImageUrl =
    row.GoogleWalletImage || row.googleWalletImage || row.GoogleWalletImageUrl || row.googleWalletImageUrl || ''
  const appleLogoUrl = row.ApplePassLogo || row.applePassLogo || row.AppleWalletLogoUrl || row.appleWalletLogoUrl || ''
  const googleLogoUrl =
    row.GooglePassLogo || row.googlePassLogo || row.GoogleWalletLogoUrl || row.googleWalletLogoUrl || ''

  // Fall back to ad details' default images if CSV doesn't have them
  const finalAppleImageUrl = appleImageUrl || passDetails.appleWalletImage || ''
  const finalGoogleImageUrl = googleImageUrl || passDetails.googleWalletImage || ''
  const finalAppleLogoUrl = appleLogoUrl || passDetails.applePassLogo || ''
  const finalGoogleLogoUrl = googleLogoUrl || passDetails.googlePassLogo || ''

  if (!finalAppleImageUrl && !finalGoogleImageUrl) {
    throw new Error('AppleWalletImageUrl or GoogleWalletImageUrl is required in CSV row')
  }

  // Get uploaded image paths from cache, or use original URLs if not uploaded
  const appleImageCacheKey = `${globalIndex}-appleImage`
  const googleImageCacheKey = `${globalIndex}-googleImage`
  const appleLogoCacheKey = `${globalIndex}-appleLogo`
  const googleLogoCacheKey = `${globalIndex}-googleLogo`

  const appleWalletImagePath = imageUploadCache.get(appleImageCacheKey)
  const googleWalletImagePath = imageUploadCache.get(googleImageCacheKey)
  const appleWalletLogoPath = imageUploadCache.get(appleLogoCacheKey)
  const googleWalletLogoPath = imageUploadCache.get(googleLogoCacheKey)

  // Use uploaded paths if available, otherwise fall back to original URLs
  // For logos: use cache if available, otherwise use original URL (which may already be uploaded)
  const finalAppleImagePath = appleWalletImagePath || finalAppleImageUrl
  const finalGoogleImagePath = googleWalletImagePath || finalGoogleImageUrl
  const finalAppleLogoPath = appleWalletLogoPath || finalAppleLogoUrl
  const finalGoogleLogoPath = googleWalletLogoPath || finalGoogleLogoUrl

  // Parse back fields from CSV row
  const backFields = parseBackFieldsFromRow(row)

  // Parse notifications from CSV row
  const walletNotifications = parseNotificationsFromRow(row)

  // Get dates from CSV or use defaults (support both lowercase and TitleCase)
  const startDateValue = row.startDate || row.StartDate || rowAdDetails?.startTime
  const endDateValue = row.endDate || row.EndDate || rowAdDetails?.expiryTime

  // For start date: if not provided, use current date at start of day
  // For end date: if not provided, use start date + default duration at end of day
  const defaultStartDate = startDateValue || new Date()
  const defaultEndDate =
    endDateValue ||
    new Date(
      (startDateValue ? new Date(startDateValue).getTime() : Date.now()) +
        DEFAULT_AD_DURATION_DAYS * 24 * 60 * 60 * 1000
    )

  const startDate = convertToISOString(defaultStartDate, false)
  const endDate = convertToISOString(defaultEndDate, true)
  const title = row.title || row.Title || rowAdDetails?.title || 'Ad'

  // Get ad details from CSV or use ad details (support both lowercase and TitleCase)
  const passColor = row.passColor || row.PassColor || passDetails.passColor || '#000000'
  const fontColor = row.fontColor || row.FontColor || passDetails.fontColor || '#FFFFFF'
  const appStoreId = row.appStoreId || row.AppStoreId || String(passDetails.appStoreId || '')
  const playStoreUrl = row.playStoreUrl || row.PlayStoreUrl || passDetails.playStoreUrl || ''
  const clickThroughUrl = row.clickthroughUrl || row.ClickthroughUrl || passDetails.clickThroughUrl || ''

  // Parse offer redemption from CSV if available
  // Check if any offerRedemption columns exist in the CSV
  const hasOfferRedemptionColumn =
    !!(row['PassOfferRedemption.Type'] || row['passOfferRedemption.type']) ||
    !!(row['PassOfferRedemption.Value'] || row['passOfferRedemption.value']) ||
    !!(row['PassOfferRedemption.Url'] || row['passOfferRedemption.url'])

  const offerRedemptionType =
    row['PassOfferRedemption.Type'] || row['passOfferRedemption.type'] || offerRedemption.codeType
  // PassOfferRedemption.Value maps to offerName
  const offerRedemptionName =
    row['PassOfferRedemption.Value'] || row['passOfferRedemption.value'] || offerRedemption.offerName || ''
  // PassOfferRedemption.Url maps to offerCodeUrl
  const offerRedemptionUrl =
    row['PassOfferRedemption.Url'] || row['passOfferRedemption.url'] || offerRedemption.offerCodeUrl || ''

  // Generate a 4-character alphanumeric googleAdId
  const googleAdId = generateGoogleAdId()

  // Build ad object with new structure
  const ad: BulkPassRequest = {
    advertId: rowAdvertId,
    title,
    startTime: startDate,
    expiryTime: endDate,
    googleAdId,
    imagePath: finalAppleImagePath || finalGoogleImagePath || '',
    categories: [
      // Default categories if not provided in CSV
      { name: 'Food & Beverage' },
      { name: 'Beverages (Alcoholic & Non-Alcoholic)' },
    ],
    wrapperDetails: {
      wrapperType: 'DEFAULT',
    },
    passDetails: {
      passColor,
      fontColor,
      AppleWalletImage: finalAppleImagePath || '',
      GoogleWalletImage: finalGoogleImagePath || '',
      ApplePassLogo: finalAppleLogoPath || '',
      GooglePassLogo: finalGoogleLogoPath || '',
      appStoreId,
      playStoreUrl,
      clickThroughUrl,
      offerRedemption: {
        enable: hasOfferRedemptionColumn,
        codeType: offerRedemptionType === 'QR_CODE' || offerRedemptionType === 'qr_code' ? 'QR_CODE' : 'BARCODE_128',
        offerName: offerRedemptionName,
        offerCodeUrl: offerRedemptionUrl,
      },
      appleWalletBackFields: backFields.appleWalletBackFields.length > 0 ? backFields.appleWalletBackFields : undefined,
      googleWalletBackFields:
        backFields.googleWalletBackFields.length > 0 ? backFields.googleWalletBackFields : undefined,
      googleWalletBackLinks: backFields.googleWalletBackLinks.length > 0 ? backFields.googleWalletBackLinks : undefined,
    },
    walletNotifications: walletNotifications.length > 0 ? walletNotifications : undefined,
  }

  return ad
}
